import { createApi } from '@reduxjs/toolkit/query/react';
import { Company } from '@typeRoots/common/company';
import { setCompany, saveCompany, clearCompany, updateCompany } from '../slice/companySlice';
import { baseQueryWithProgress } from 'src/helpers/fetchQueryHelper';

export const companyApi = createApi({
  // Unique key for the API slice in Redux state
  reducerPath: 'companyApi',

  // Configure base query settings, including the base URL for all requests
  baseQuery: baseQueryWithProgress,

  // Define cache tag types for automatic cache invalidation
  tagTypes: ['Company', 'Notes'],

  // Define API endpoints (queries and mutations)

  endpoints: (builder) => ({
    getCompany: builder.query<Company, number>({
      query: (id) => `/companies/${id}`,
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        try {
          const { data: company } = await queryFulfilled;
          dispatch(setCompany(company));
        } catch {
          console.log('Get Company data error');
        }
      },
      // Tagging Companies to automatically refresh this cache when needed
      providesTags: ['Company'],
    }),

    // Get company by DOT and MC number
    getCompanyByDotMc: builder.query<
      { company_exists: boolean; company?: Company },
      { dotNumber: string; mcNumber: string; tpJwt: string }
    >({
      query: ({ dotNumber, mcNumber, tpJwt }) => ({
        url: `/companies/from-dot/`,
        method: 'POST',
        body: {
          dotNumber,
          mcNumber,
          tpJwt,
        },
      }),
      async onQueryStarted(data, { dispatch, queryFulfilled }) {
        try {
          const { data: companyData } = await queryFulfilled;
          if (companyData.company_exists && companyData.company) {
            // Populate the Redux store with the fetched company data
            dispatch(setCompany(companyData.company));
          } else {
            // Reset the store to an empty company state
            dispatch(clearCompany());
          }
        } catch (error) {
          console.log('Failed to fetch company data', error);
        }
      },
    }),

    // Create company endpoint
    createCompany: builder.mutation<Company, Partial<Company>>({
      query: (newCompany) => ({
        url: '/companies',
        method: 'POST',
        body: newCompany,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      async onQueryStarted(_company, { dispatch, queryFulfilled }) {
        try {
          const { data: companyData } = await queryFulfilled;
          dispatch(updateCompany(companyData));
          dispatch(saveCompany()); // New company created
        } catch {
          console.log('Create company error');
        }
      },
    }),

    // Update company endpoint
    updateCompany: builder.mutation<Company, { id: number; updateCompanyData: Partial<Company> }>({
      query: ({ id, updateCompanyData }) => ({
        url: `/companies/${id}`,
        method: 'PATCH',
        body: JSON.stringify(updateCompanyData),
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(updateCompany(params.updateCompanyData));
          dispatch(saveCompany()); // This action
          console.log('Company data updated on backend as well:');
        } catch {
          console.log('Update Company data error');
        }
      },
    }),

    // Delete a user by ID
    deleteCompany: builder.mutation<void, number>({
      query: (id) => ({
        url: `/companies/${id}`,
        method: 'DELETE',
      }),
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(clearCompany());
        } catch {
          console.log('Delete Company error');
        }
      },
      // Invalidate the cache for the deleted company and refetch the list
      invalidatesTags: ['Company'],
    }),

    // Create company endpoint
    addDataFilledBy: builder.mutation<Company, { companyId: number; dataFilledBy: string }>({
      query: ({ companyId, dataFilledBy }) => ({
        url: `/companies/add-data-filled-by/${companyId}`,
        method: 'POST',
        body: { dataFilledBy },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      async onQueryStarted(_params, { dispatch, queryFulfilled }) {
        try {
          const { data: companyData } = await queryFulfilled;
          dispatch(updateCompany(companyData));
          dispatch(saveCompany());
        } catch {
          console.log('dataFilledBy mutation error');
        }
      },
    }),

    // Fetch company notes
    getCompanyNotes: builder.query<string, string>({
      query: (companyId: string) => ({
        url: `companies/note/${companyId}`,
        responseHandler: 'text', // Expect plain text response
      }),
      transformResponse: (response: string) => {
        // If response is a string, trim and return it
        if (typeof response === 'string') {
          return response?.trim();
        }
        return '';
      },
      providesTags: ['Notes'],
    }),

    // Update company notes endpoint
    updateCompanyNotes: builder.mutation<
      void,
      { companyId: string; note: string; producerFormId: string }
    >({
      query: ({ companyId, note, producerFormId }) => ({
        url: `companies/add-note/${companyId}`,
        method: 'POST',
        body: { note, producerFormId },
      }),
      invalidatesTags: ['Notes'],
    }),
  }),
});

// Export generated hooks for each endpoint to use them in components
export const {
  useGetCompanyQuery, // Use this when you want data to be fetched automatically as the component mounts or when the query parameters change
  useLazyGetCompanyQuery, // Use this for manual fetching
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useGetCompanyByDotMcQuery, // For fetching company by DOT and MC automatically
  useLazyGetCompanyByDotMcQuery, // For manual fetching of company by DOT and MC
  useAddDataFilledByMutation,
  useGetCompanyNotesQuery,
  useUpdateCompanyNotesMutation,
} = companyApi;
